import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import NoSsr from '../NoSsr/NoSsr';
import * as S from '@/components/Shop/Shop.styles';
import {
  buildGalleryItems,
  isTextOverflowing,
} from '@/components/Shop/ShopView.helpers';
import { GalleryModal } from '@/components/Shop/ShopGallery/ShopGallery';
import { ShopGalleryPreview } from '@/components/Shop/ShopGallery/ShopGalleryPreview';
import { PhoneIcon } from '@/components/UI/Icons/Icons';
import { PhonesBottomSheet } from '@/components/PhonesBottomSheet/PhonesBottomSheet';
import { ShopViewProps, ShopCategory } from '@/api/types/shop.types';
import 'yet-another-react-lightbox/styles.css';
import useScreenSize from '@/hooks/useScreenSize';
import { useOpenContacts } from '@/hooks/useOpenContacts';
import { cleanPhoneNumber, setSuffix } from '@/utils/helpers';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { ShopAdsSection } from '@/components/Shop/ShopAds/ShopAdsSection';
import { ShopInfoSkeleton } from '@/components/Shop/ShopSkeleton/ShopInfoSkeleton';
import { useShopFavorite } from '@/hooks/useShopFavorite';
import { Toast } from '@/components/UI/Toast';

export const ShopView: React.FC<ShopViewProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const ref = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [canOpen, setCanOpen] = useState(false);
  const { isLarge } = useScreenSize();
  const { getPhoneNumbers, phoneNumbers, isContactsOpen, toggleContacts } =
    useOpenContacts(true);
  const showPhonesInline = phoneNumbers.length > 0;

  const { isFavorited: isFavoritedLocal, toggleFavorite: handleSubscribe } =
    useShopFavorite({ shop: data });

  const [galleryIndex, setGalleryIndex] = useState(-1);

  useEffect(() => {
    if (ref.current && !isOpen) {
      setCanOpen(isTextOverflowing(ref.current));
    }
  }, [isOpen, data?.description]);

  useEffect(() => {
    return () => {
      document.body.style.removeProperty('position');
    };
  }, []);

  const userLegacyId = data?.user?.legacyId;

  const openGoogleMaps = () => {
    if (data?.latitude && data?.longitude) {
      const url = `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`;
      window.open(url, '_blank');
    }
  };

  if (!data) {
    return <ShopInfoSkeleton />;
  }

  const allCategory: ShopCategory = {
    id: '',
    name: isLarge ? t('shop.categoriesDesk') : t('shop.categoriesMob'),
    slug: 'all',
    path: '',
    count: data.adsCount,
  };

  const categories = [allCategory, ...(data.categories ?? [])];

  function toggle() {
    setIsOpen(true);
  }

  const toggleGallery = (index: number) => {
    if (index === -1) {
      document.body.style.removeProperty('position');
    } else {
      document.body.style.setProperty('position', 'fixed');
    }
    return setGalleryIndex(index);
  };

  return (
    <>
      <S.BreadcrumbsContainer>
        <S.Breadcrumbs>
          <CustomLink href="/shops">{t('shop.breadcrumb')}</CustomLink>
          <S.BreadcrumbsShopName>{data.name}</S.BreadcrumbsShopName>
        </S.Breadcrumbs>
      </S.BreadcrumbsContainer>

      {data.cover?.url && (
        <S.CoverWrapper>
          <S.CoverBackground $bg={data.cover.url} />
          <S.CoverImage src={data.cover.url} alt="shop-cover" />
        </S.CoverWrapper>
      )}

      <S.ContentContainer>
        <S.ContentTop>
          <S.TopSection>
            <S.LeftTop>
              {data.logo?.url && (
                <S.LogoWrapper $bgImage={data.logo.url}>
                  <S.LogoImage src={data.logo.url} alt="shop-logo" />
                </S.LogoWrapper>
              )}

              <S.ShopTextGroup>
                <S.ShopName>{data.name}</S.ShopName>
                {(data.workingDays || data.workingHours) && (
                  <S.ShopWorkTime>
                    {data.workingDays} {data.workingHours}
                  </S.ShopWorkTime>
                )}
                <NoSsr>
                  <S.ShopStats>
                    {t(setSuffix(i18n, String(data.adsCount), 'common.n_ads'), {
                      n: data.adsCount,
                    })}{' '}
                    •{t('shop.views', { hits: data.hits })} •
                    {t('shop.favorites', { count: data.favoriteShopsCount })}
                  </S.ShopStats>
                </NoSsr>
              </S.ShopTextGroup>
            </S.LeftTop>
            <NoSsr>
              {isLarge ? (
                <S.ShopRightContainer>
                  <S.ShopFavorite
                    $isFavoritedLocal={isFavoritedLocal}
                    onClick={() => handleSubscribe()}
                    data-testid="shop-subscribe"
                  >
                    {isFavoritedLocal
                      ? t('shop.added_bookmark_shop')
                      : t('shop.bookmark_shop')}
                  </S.ShopFavorite>
                  <S.ContactBox>
                    {!showPhonesInline ? (
                      <S.FloatingButton
                        onClick={() => {
                          getPhoneNumbers({ shopId: data.id });
                        }}
                      >
                        <PhoneIcon /> {t('shop.show_number')}
                      </S.FloatingButton>
                    ) : (
                      <S.PhoneList>
                        {phoneNumbers.map((phone, idx) => (
                          <a href={`tel:${cleanPhoneNumber(phone)}`} key={idx}>
                            {phone}
                          </a>
                        ))}
                      </S.PhoneList>
                    )}
                  </S.ContactBox>
                </S.ShopRightContainer>
              ) : (
                <S.ContactBox>
                  <S.ShopFavorite
                    $isFavoritedLocal={isFavoritedLocal}
                    onClick={() => handleSubscribe()}
                    data-testid="shop-subscribe"
                  >
                    {isFavoritedLocal
                      ? t('shop.added_bookmark_shop')
                      : t('shop.bookmark_shop')}
                  </S.ShopFavorite>
                  <S.FloatingButton
                    onClick={() => {
                      getPhoneNumbers({ shopId: data.id });
                    }}
                  >
                    <PhoneIcon />
                    <S.PhoneText>{t('common.call')}</S.PhoneText>
                  </S.FloatingButton>
                </S.ContactBox>
              )}
            </NoSsr>
          </S.TopSection>

          <S.ShopExtraInfo>
            {data.promo && <S.ShopPromo>{data.promo}</S.ShopPromo>}
            {data.description && (
              <S.ShopDescription ref={ref} $isOpen={isOpen} $variant="4">
                {data.description}
              </S.ShopDescription>
            )}
            {canOpen && !isOpen && (
              <S.Toggle $variant="2" onClick={toggle}>
                {t('common.read_more')}
              </S.Toggle>
            )}
            {data.address && (
              <S.ShopAddress onClick={openGoogleMaps}>
                {data.address}
              </S.ShopAddress>
            )}
          </S.ShopExtraInfo>

          {(data.photos?.length > 0 || data.videos?.length > 0) && (
            <ShopGalleryPreview
              items={buildGalleryItems(data.photos, data.videos, t)}
              onOpenGallery={index => toggleGallery(index)}
            />
          )}

          {galleryIndex >= 0 &&
            (data.photos?.length > 0 || data.videos?.length > 0) && (
              <GalleryModal
                items={buildGalleryItems(data.photos, data.videos, t)}
                activeIndex={galleryIndex}
                setActiveIndex={setGalleryIndex}
                onClose={() => toggleGallery(-1)}
                shopName={data.name}
                workingDays={data.workingDays}
                shopWorkTime={data.workingHours}
                phoneNumbers={phoneNumbers}
                getPhoneNumbers={() => getPhoneNumbers({ shopId: data.id })}
                toggleContacts={toggleContacts}
              />
            )}
        </S.ContentTop>

        <S.ContentBottom>
          <S.ContentOffers>
            {t('shop.ads_title', { name: data.name })}
          </S.ContentOffers>
          {userLegacyId && data.adsCount > 0 ? (
            <ShopAdsSection
              userLegacyId={userLegacyId}
              categories={categories}
            />
          ) : (
            <S.ShopAdsEmpty>{t('shop.ads_empty')}</S.ShopAdsEmpty>
          )}
        </S.ContentBottom>
      </S.ContentContainer>

      {!isLarge && (
        <PhonesBottomSheet
          isOpen={isContactsOpen}
          close={toggleContacts}
          phones={phoneNumbers}
          color={theme.palette.system.green}
        />
      )}
      <Toast />
    </>
  );
};
